import { useTranslationContext } from "@providers/TranslationProvider";
import cx from "classnames";
import map from "lodash/map";
import Link from "@components/Link";
import { Fragment, useEffect, useState } from "react";
import Down from "@components/Icons/Down";
import Facebook from "@components/Icons/Facebook";
import Instagram from "@components/Icons/Instagram";
import LogoVertical from "@components/Logo/VerticalYellow";
import Expand from "@components/Expand";
import Icons from "@gstyles/styleguide/icons";
import styles from "./Footer2.module.scss";
import { useRouteContext } from "@providers/RouteProvider";
import { useLocomotiveContext } from "@providers/LocomotiveProvider";
import { usePageContext } from "@providers/PageProvider";

interface Props {
  hideSocialIcons?: boolean;
  // Legacy prop kept for backwards compatibility; CMS footerData from pageContext takes priority
  data?: {
    name?: string;
    address?: string;
    email?: string;
    phone?: string;
    facebook?: string;
    instagram?: string;
  };
}

function Footer(props: Props) {
  const { scroll } = useLocomotiveContext();
  const { i18n } = useTranslationContext();
  const { hideSocialIcons = false, data } = props;
  const { footerData } = usePageContext() as any;
  const { router } = useRouteContext();

  // CMS data takes priority over legacy data prop, then i18n/hardcoded fallbacks
  const name = footerData?.name || data?.name || i18n.t("Footer.name");
  // Repeatable address blocks (group footer) take priority over legacy single fields
  const addressBlocks: Array<{ address: string; phone: string; email: string }> =
    footerData?.addressBlocks?.length > 0
      ? footerData.addressBlocks
      : [
          {
            address: footerData?.address || data?.address || i18n.t("Footer.address10"),
            email: footerData?.email || data?.email || "info@amanaki.vn",
            phone: footerData?.phone || data?.phone || "(+84) 82 362 4488",
          },
        ];
  const facebook =
    footerData?.facebookLink ||
    data?.facebook ||
    "https://www.facebook.com/amanaki.thaodien";
  const instagram =
    footerData?.instagramLink ||
    data?.instagram ||
    "https://www.instagram.com/amanaki_thaodien";
  const copyrightText =
    footerData?.copyrightText || i18n.t("Footer.copyright");

  // Nav sections: use CMS data if available, otherwise fall back to hardcoded i18n structure
  const navOurHotels = footerData?.ourHotels?.sectionTitle
    ? footerData.ourHotels
    : {
        sectionTitle: i18n.t("Footer.ourHotels"),
        links: [
          {
            title: i18n.t("Footer.thaoDien"),
            link: router.toUrl("amanaki-thao-dien"),
          },
          {
            title: i18n.t("Footer.boutiqueSaigon"),
            link: router.toUrl("amanaki-boutique-saigon"),
          },
        ],
      };

  const navGetToKnowUs = footerData?.getToKnowUs?.sectionTitle
    ? footerData.getToKnowUs
    : {
        sectionTitle: i18n.t("Footer.getToKnowUs"),
        links: [
          { title: i18n.t("Footer.whoWeAre"), link: router.toUrl("about-us") },
          {
            title: i18n.t("Footer.sustainabilityImpact"),
            link: router.toUrl("about-sustainability"),
          },
          {
            title: i18n.t("Footer.careers"),
            link: router.toUrl("about-careers"),
          },
        ],
      };

  const navGetInTouch = footerData?.getInTouch?.sectionTitle
    ? footerData.getInTouch
    : {
        sectionTitle: i18n.t("Footer.getInTouch"),
        links: [
          {
            title: i18n.t("Footer.contactUs"),
            link: router.toUrl("contact"),
          },
        ],
      };

  const [_showNav, $showNav] = useState([
    "ourHotels",
    "getToKnowUs",
    "explore",
    "getInTouch",
  ]);

  useEffect(() => {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      $showNav([]);
    }
  }, []);

  return (
    <div data-scroll-section className={styles.section}>
      <div className={styles.mainInfoFooterWrapper}>
        <footer className={styles.mainInfoFooter}>
          <LogoVertical className={cx(styles.logo)} />

          <div className={cx(styles.addressGroup)}>
            <div className={cx(styles.address)}>
              <span className={cx(styles.name)}>{name}</span>
            </div>

            <div className={cx(styles.addressTable)}>
              {map(addressBlocks, (block, idx) => (
                <Fragment key={idx}>
                  <span className={cx(styles.value)}>{block.address}</span>
                  <div className={cx(styles.phone)}>
                    <Icons
                      name="phone-outline"
                      color="#af915e"
                      className={cx(styles.icon)}
                      alt="phone"
                    />
                    <div className={cx(styles.value)}>
                      <Link href={`tel:${block.phone}`} ariaLabel={"phone"}>
                        {i18n.t("Footer.tel")}: {block.phone}
                      </Link>
                    </div>
                  </div>
                  <div className={cx(styles.phone)}>
                    <Icons
                      name="mail-outline"
                      color="#af915e"
                      className={cx(styles.icon)}
                      alt="mail"
                    />
                    <div className={cx(styles.value)}>
                      <Link href={`mailto:${block.email}`} ariaLabel={"mail"}>
                        {block.email}
                      </Link>
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>

          <div className={cx(styles.socialGroup)}>
            {!hideSocialIcons && (
              <>
                <Link
                  href={instagram}
                  target="_blank"
                  rel="noopener"
                  ariaLabel="amanaki instagram"
                >
                  <Instagram />
                </Link>
                <Link
                  href={facebook}
                  target="_blank"
                  rel="noopener"
                  ariaLabel="amanaki facebook"
                >
                  <Facebook />
                </Link>
              </>
            )}
          </div>

          <div className={cx(styles.expandInfo)}>
            <Expand
              showDesktop
              label={
                <div className={cx(styles.label)}>
                  <div className={cx(styles.text)}>
                    {navOurHotels.sectionTitle}
                  </div>
                  <div className={cx(styles.icon)}>
                    <Down />
                  </div>
                </div>
              }
              content={
                <div className={cx(styles.content)}>
                  {map(navOurHotels.links, (item, idx) => (
                    <Link href={item.link} key={idx}>
                      <div className={cx(styles.value)}>{item.title}</div>
                    </Link>
                  ))}
                </div>
              }
            />
          </div>

          <div
            className={cx(styles.expandInfo)}
            onClick={() => $showNav(["getToKnowUs"])}
          >
            <Expand
              showDesktop
              label={
                <div className={cx(styles.label)}>
                  <div className={cx(styles.text)}>
                    {navGetToKnowUs.sectionTitle}
                  </div>
                  <div className={cx(styles.icon)}>
                    <Down />
                  </div>
                </div>
              }
              content={
                <div className={cx(styles.content)}>
                  {map(navGetToKnowUs.links, (item, idx) => (
                    <Link href={item.link} key={idx}>
                      <div className={cx(styles.value)}>{item.title}</div>
                    </Link>
                  ))}
                </div>
              }
            />
          </div>

          <div className={cx(styles.expandInfo)}>
            <Expand
              showDesktop
              label={
                <div className={cx(styles.label)}>
                  <div className={cx(styles.text)}>
                    {navGetInTouch.sectionTitle}
                  </div>
                  <div className={cx(styles.icon)}>
                    <Down />
                  </div>
                </div>
              }
              content={
                <div className={cx(styles.content)}>
                  {map(navGetInTouch.links, (item, idx) => (
                    <Link href={item.link} key={idx}>
                      <div className={cx(styles.value)}>{item.title}</div>
                    </Link>
                  ))}
                </div>
              }
            />
          </div>

          <div className={cx("absolute", styles.backToTop)}>
            <div
              className="flex items-center rotate-90"
              onClick={() => {
                scroll.scrollTo(0, { duration: 500 });
              }}
            >
              <div className="mr-6 -ml-2 -rotate-90">
                <Icons name="collapse-up" size={40} />
              </div>
              <div className="uppercase ">{i18n.t("Button.backToTop")}</div>
            </div>
          </div>
        </footer>
      </div>
      <div className={cx(styles.copyrightSectionWrapper)}>
        <div className={cx(styles.copyrightSection)}>
          <div>{copyrightText}</div>
          <span>
            <Link
              target={"_blank"}
              href={router.toUrl("privacy", {
                legalNoticeType: "PrivacyPolicy",
              })}
              ariaLabel={"privacy policy"}
            >
              {i18n.t("Footer.privacyPolicy")}
            </Link>
          </span>
          <span>
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(new CustomEvent("openCookiePreferences"))
              }
              className={cx(styles.cookiePreferencesLink)}
              aria-label={i18n.t("CookieConsent.cookiePreferences")}
            >
              {i18n.t("CookieConsent.cookiePreferences")}
            </button>
          </span>
        </div>
        <div className={cx("", styles.backToTopMobile)}>
          <div
            className="flex items-center rotate-90"
            style={{ width: "148px" }}
            onClick={() => {
              scroll.scrollTo(0, { duration: 500 });
            }}
          >
            <div className="mr-2 -rotate-90">
              <Icons name="collapse-up" size={40} />
            </div>
            <div className="uppercase ">{i18n.t("Button.backToTop")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
